<template>
  <v-container
    fill-height
    fluid
    class="signup-bg pa-0 ma-0"
    style="width: 100vw; height: 100vh"
  >
    <v-row
      align="center"
      justify="center"
      class="ma-0"
      style="min-height: 100vh"
    >
      <v-col cols="12" sm="8" md="4" class="pa-4">
        <v-card class="pa-6 text-center elevation-12 rounded-xl">
          <v-img
            src="@/assets/nikah-connect-logo.png"
            contain
            max-width="120"
            class="mx-auto mb-3"
          ></v-img>

          <h2 class="font-weight-bold mb-4">Welcome to Nikah Connect</h2>
          <p class="grey--text text--darken-1 mb-6">
            Begin your journey to connect families with trust and values.
          </p>

          <v-form ref="form" v-model="valid" lazy-validation>
            <!-- Full Name -->
            <v-text-field
              v-model="user.name"
              label="Full Name"
              prepend-inner-icon="mdi-account"
              :rules="[(v) => !!v || 'Name is required']"
              outlined
              autofocus
              dense
              @blur="onNameBlur"
            />

            <!-- Email -->
            <v-text-field
              v-model="user.email"
              label="Email Address"
              prepend-inner-icon="mdi-email"
              :rules="[(v) => /.+@.+/.test(v) || 'E-mail must be valid']"
              outlined
              dense
            ></v-text-field>

            <!-- Password -->
            <v-text-field
              v-model="user.password"
              label="Password"
              prepend-inner-icon="mdi-lock"
              :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append="showPassword = !showPassword"
              :type="showPassword ? 'text' : 'password'"
              :rules="[(v) => v.length >= 6 || 'Minimum 6 characters']"
              outlined
              dense
            ></v-text-field>

            <!-- Gender Selection -->
            <div class="text-left mb-3">
              <!-- <label class="font-weight-medium mb-2">Select Gender</label> -->

              <v-btn-toggle
                v-model="user.gender"
                mandatory
                dense
                class="gender-toggle"
                tile
              >
                <v-btn
                  value="male"
                  depressed
                  class="gender-btn"
                  :class="{ active: user.gender === 'male' }"
                >
                  <v-icon left small>mdi-human-male</v-icon>
                  Male
                </v-btn>

                <v-btn
                  value="female"
                  depressed
                  class="gender-btn"
                  :class="{ active: user.gender === 'female' }"
                >
                  <v-icon left small>mdi-human-female</v-icon>
                  Female
                </v-btn>
              </v-btn-toggle>
            </div>

            <!-- Date of Birth -->
            <div class="text-left mb-4">
              <label class="font-weight-medium mb-1">Date of Birth</label>
              <v-layout row wrap>
                <v-flex xs4>
                  <v-text-field
                    v-model="dobMonth"
                    label="MM"
                    type="number"
                    outlined
                    dense
                    maxlength="2"
                    @input="autoAdvanceDob"
                  ></v-text-field>
                </v-flex>
                <v-flex xs4>
                  <v-text-field
                    v-model="dobDay"
                    label="DD"
                    type="number"
                    outlined
                    dense
                    maxlength="2"
                    @input="autoAdvanceDob"
                  ></v-text-field>
                </v-flex>
                <v-flex xs4>
                  <v-text-field
                    v-model="dobYear"
                    label="YYYY"
                    type="number"
                    outlined
                    dense
                    maxlength="4"
                    @input="updateDob"
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <p
                class="text-caption font-weight-bold grey--text text--darken-1 mt-n2 mb-2"
              >
                You must be 18 years old or older to create a profile.
              </p>
            </div>

            <v-row>
              <v-col cols="12">
                <v-layout align-center class="mt-2 mb-2">
                  <v-checkbox
                    v-model="user.acceptTerms"
                    :rules="[
                      (v) => !!v || 'You must accept the Terms and Conditions',
                    ]"
                    class="ml-3"
                    dense
                    hide-details="auto"
                    @change="termsError = ''"
                  ></v-checkbox>

                  <div class="ml-2" style="user-select: none">
                    I agree to the
                    <span
                      class="text-primary font-weight-medium"
                      style="cursor: pointer; text-decoration: underline"
                      @click.stop.prevent="openTerms"
                    >
                      Terms and Conditions
                    </span>
                  </div>
                </v-layout>
              </v-col>
            </v-row>

            <!-- Terms and Conditions -->

            <!-- Inline Error Message -->
            <p
              v-if="termsError"
              class="error--text text-caption mt-n2 ml-10"
              style="font-weight: 500"
            >
              {{ termsError }}
            </p>

            <!-- Submit Button -->
            <v-btn
              block
              color="primary"
              class="mt-5 py-2 font-weight-bold"
              :loading="loading"
              @click="doSignUp"
            >
              Sign Up
            </v-btn>

            <div class="mt-3 grey--text">
              Already have an account?
              <router-link to="/login" class="font-weight-medium"
                >Login</router-link
              >
            </div>
          </v-form>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar" timeout="4000" color="success">
      {{ snackbarText }}
      <v-btn text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import authService from "@/modules/Authentication/service.js";
import axios from "axios";

export default {
  data() {
    return {
      valid: false,
      termsError: "",
      showPassword: false,
      loading: false,
      snackbar: false,
      snackbarText: "",
      user: {
        name: "",
        email: "",
        password: "",
        dob: "",
        gender: "",
        acceptTerms: false,
      },
      dobMonth: "",
      dobDay: "",
      dobYear: "",
    };
  },
  methods: {
    onNameBlur() {
      if (!this.user.name) return;
      clearTimeout(this._genderTimer);
      this._genderTimer = setTimeout(() => {
        this.predictGender();
      }, 400); // slight delay for stability
    },

    async predictGender() {
      try {
        const firstName = this.user.name.split(" ")[0];
        if (!firstName) return;

        const { data } = await axios.get(
          `https://api.genderize.io/?name=${firstName}`
        );

        // Only auto-select if high confidence (probability > 0.8)
        if (data && data.gender && data.probability > 0.8) {
          this.user.gender = data.gender.toLowerCase();
        } else {
          this.user.gender = ""; // reset if no confident prediction
        }
      } catch (e) {
        console.warn("Gender prediction failed:", e.message);
      }
    },

    autoAdvanceDob() {
      // if month/day/year filled properly, update main dob string
      if (this.dobMonth && this.dobDay && this.dobYear) this.updateDob();
    },

    updateDob() {
      if (
        this.dobMonth.length === 2 &&
        this.dobDay.length === 2 &&
        this.dobYear.length === 4
      ) {
        const mm = this.dobMonth.padStart(2, "0");
        const dd = this.dobDay.padStart(2, "0");
        const yyyy = this.dobYear;
        const fullDate = `${yyyy}-${mm}-${dd}`;
        this.user.dob = fullDate;
      }
    },

    validateAge() {
      if (!this.user.dob) return false;
      const dob = new Date(this.user.dob);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
      return age >= 18;
    },

    async doSignUp() {
      const isValid = this.$refs.form.validate();
      if (!isValid) {
        // 👇 check if the checkbox is false and show inline message
        if (!this.user.acceptTerms) {
          this.termsError = "You must accept the Terms and Conditions";
        }
        return;
      }

      if (!this.validateAge()) {
        this.$swal(
          "Age Restriction",
          "You must be at least 18 years old to create a profile.",
          "warning"
        );
        return;
      }

      if (!this.user.gender) {
        this.$swal("Required", "Please select your gender.", "warning");
        return;
      }

      this.termsError = ""; // clear old error
      this.loading = true;

      const payload = {
        name: this.user.name,
        emailAddress: this.user.email,
        password: this.user.password,
        dob: this.user.dob,
        gender: this.user.gender,
        acceptedTerms: this.user.acceptTerms,
      };

      authService
        .signUp(payload)
        .then(() => {
          this.loading = false;
          this.$swal(
            "Account Created!",
            `A verification email has been sent to ${payload.emailAddress}. Please check your inbox.`,
            "success"
          );
          this.$router.push("/login");
        })
        .catch((error) => {
          this.loading = false;
          console.error("signup error", error);
          this.$swal(
            "Signup failed!",
            error?.data?.message || "Please try again later.",
            "error"
          );
        });
    },
  },
};
</script>

<style scoped>
.signup-bg {
  background: linear-gradient(145deg, #f8fafc, #eaf3ef);
  min-height: 100vh;
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  overflow: hidden;
}
.v-card {
  border-radius: 20px;
}

.gender-toggle {
  display: flex;
  justify-content: space-between;
  width: 100%;
  border-radius: 8px;
}

.gender-btn {
  flex: 1;
  text-transform: none;
  font-weight: 500;
  color: #2a6465 !important;
  background-color: #f5f7f6 !important;
  border: 1px solid #d2d8d6 !important;
  transition: all 0.2s ease-in-out;
}

.gender-btn.active {
  background-color: #2a6465 !important;
  color: #fff !important;
  border-color: #2a6465 !important;
}

.gender-btn + .gender-btn {
  margin-left: 6px !important;
}

/* Ensure full screen on desktop */
@media (min-width: 768px) {
  .signup-bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
  }
}
</style>